import React, { useEffect, useMemo, useState } from 'react';
import { DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Alert,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  message,
  Radio,
  Select,
  Space,
  Spin,
  Tooltip,
  Popover,
} from 'antd';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { benefitCouponOptionsV2, benefitCreateV2, benefitDetailV2, benefitUpdateV2 } from '@/api/activity/activity';
import ImageUploader from '@/component/ImageUploader';
import { defaultEditorConfig, defaultToolbarConfig } from '@/utils/editorConfig';
import popupButtonTextImage from '@/assets/images/popupButtonText.png';

const { RangePicker } = DatePicker;
const MAX_BENEFIT_COUNT = 3;
const BENEFIT_TYPE_OPTIONS = [
  { label: '满减券', value: 'coupon_full_reduction' },
  { label: '实物券', value: 'physical_coupon' },
];
const BENEFIT_INDEX_TEXT = ['一', '二', '三'];
const INPUT_COUNT_PROPS = {
  formatter: ({ count, maxLength }: { count: number; maxLength?: number }) => `${count}/${maxLength || 0}`,
};
const POPUP_BUTTON_TEXT_TIP = (
  <img
    src={popupButtonTextImage}
    alt="弹窗按钮文案示意图"
    style={{ width: 260, display: 'block' }}
  />
);
const POPUP_BUTTON_TEXT_LABEL = (
  <Space size={4}>
    <span>弹窗按钮文案</span>
    <Popover
      content={POPUP_BUTTON_TEXT_TIP}
      placement="right">
      <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'pointer' }} />
    </Popover>
  </Space>
);

type DrawerMode = 'create' | 'view' | 'edit';

type PetProfileV2DrawerProps = {
  open: boolean;
  mode: DrawerMode;
  activityId?: string | number;
  copyFromActivityId?: string | number;
  shopId?: string | number | null;
  platformType?: string | null;
  onClose: () => void;
  onSuccess: () => void;
};

const createDefaultBenefit = () => ({
  benefitType: 'coupon_full_reduction',
  couponId: undefined,
  reductionCouponId: undefined,
  physicalCouponId: undefined,
  benefitImage: '',
  benefitTitle: '',
  benefitSubTitle: '',
  actionButtonText: '',
  actionButtonLink: '',
  popupButtonText: '',
  popupButtonLink: '',
  customButtonEnabled: false,
  customButtonText: '',
  customButtonLink: '',
});

const createInitialValues = () => ({
  activityName: '',
  backImage: '',
  activityDesc: '',
  timeType: 'permanent',
  timeRange: undefined,
  activityStatus: 'enabled',
  benefitList: [createDefaultBenefit()],
});

const isRichTextEmpty = (html?: string) => {
  if (!html) return true;
  if (/<img\b[^>]*\bsrc\s*=\s*['"][^'"]+['"][^>]*>/i.test(html)) {
    return false;
  }
  const text = html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, '')
    .trim();
  return !text;
};

const uploadRequiredRule = (messageText: string) => ({
  validator(_: any, value: any) {
    if (!value) return Promise.reject(new Error(messageText));
    if (typeof value === 'object' && Object.keys(value).length === 0) return Promise.reject(new Error(messageText));
    return Promise.resolve();
  },
});

const PetProfileV2Drawer: React.FC<PetProfileV2DrawerProps> = ({
  open,
  mode,
  activityId,
  copyFromActivityId,
  shopId,
  platformType,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponOptions, setCouponOptions] = useState<any[]>([]);
  const [editor, setEditor] = useState<any>(null);
  const [editorHtml, setEditorHtml] = useState('');
  const toolbarConfig = useMemo(() => defaultToolbarConfig, []);
  const timeType = Form.useWatch('timeType', form);
  const isView = mode === 'view';

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy?.();
      }
    };
  }, [editor]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(createInitialValues());
    setEditorHtml('');
    if (shopId) {
      fetchCouponOptions(shopId);
    }
    const detailActivityId = mode === 'create' ? copyFromActivityId : activityId;
    if (detailActivityId) {
      fetchDetail(detailActivityId);
    }
  }, [open, mode, activityId, copyFromActivityId, shopId]);

  const fetchCouponOptions = async (activeShopId: string | number) => {
    setCouponLoading(true);
    try {
      const res: any = await benefitCouponOptionsV2({ shopId: activeShopId });
      if (res?.code === 200) {
        setCouponOptions(
          (res.data || []).map((item: any) => ({
            label: `${item.couponName || '--'}${item.minConsumption || item.amount ? `（满${item.minConsumption || 0}减${item.amount || 0}）` : ''}`,
            value: item.couponId,
          })),
        );
      } else {
        setCouponOptions([]);
      }
    } catch (error) {
      console.error(error);
      setCouponOptions([]);
    } finally {
      setCouponLoading(false);
    }
  };

  const fetchDetail = async (targetActivityId: string | number) => {
    setDetailLoading(true);
    try {
      const res: any = await benefitDetailV2({ activityId: targetActivityId });
      if (res?.code === 200 && res.data) {
        const detail = res.data;
        const benefits =
          Array.isArray(detail.benefitList) && detail.benefitList.length
            ? [...detail.benefitList]
                .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0))
                .map((item: any) => ({
                  ...createDefaultBenefit(),
                  ...item,
                  couponId: item.benefitType === 'coupon_full_reduction' ? item.couponId : undefined,
                  reductionCouponId: item.benefitType === 'coupon_full_reduction' ? item.couponId : undefined,
                  physicalCouponId: item.benefitType === 'physical_coupon' ? item.couponId : undefined,
                  customButtonEnabled: Boolean(item.customButtonEnabled),
                }))
            : [createDefaultBenefit()];
        form.setFieldsValue({
          activityName: detail.activityName,
          backImage: detail.backImage,
          activityDesc: detail.activityDesc,
          timeType: detail.timeType || 'permanent',
          timeRange:
            detail.timeType === 'specified' && detail.startTime && detail.endTime
              ? [dayjs(detail.startTime, 'YYYY-MM-DD HH:mm:ss'), dayjs(detail.endTime, 'YYYY-MM-DD HH:mm:ss')]
              : undefined,
          activityStatus: detail.activityStatus,
          benefitList: benefits,
        });
        setEditorHtml(detail.activityNotice || '');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setEditorHtml('');
    setEditor(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!shopId) {
      message.warning('请先选择店铺');
      return;
    }
    if (!platformType) {
      message.warning('缺少平台信息');
      return;
    }
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      if (isRichTextEmpty(editorHtml)) {
        message.error('请输入权益说明');
        return;
      }
      const benefits = Array.isArray(values.benefitList) ? values.benefitList : [];
      if (!benefits.length) {
        message.error('请至少添加一条权益');
        return;
      }
      if (benefits.length > MAX_BENEFIT_COUNT) {
        message.error(`权益最多 ${MAX_BENEFIT_COUNT} 条`);
        return;
      }
      const payload: any = {
        activityName: values.activityName,
        shopId,
        platformType,
        backImage: values.backImage,
        activityDesc: values.activityDesc,
        activityNotice: editorHtml,
        activityStatus: values.activityStatus,
        activityType: 'pet_profile_v2',
        timeType: values.timeType,
        benefitList: benefits.map((item: any) => ({
          benefitType: item.benefitType,
          couponId:
            item.benefitType === 'coupon_full_reduction'
              ? item.couponId || item.reductionCouponId || undefined
              : item.benefitType === 'physical_coupon'
                ? item.physicalCouponId || undefined
                : undefined,
          benefitImage: item.benefitImage,
          benefitTitle: item.benefitTitle,
          benefitSubTitle: item.benefitSubTitle,
          actionButtonText: item.actionButtonText,
          actionButtonLink: item.actionButtonLink,
          popupButtonText: item.popupButtonText,
          popupButtonLink: item.popupButtonLink,
          customButtonEnabled: Boolean(item.customButtonEnabled),
          customButtonText: item.customButtonEnabled ? item.customButtonText : undefined,
          customButtonLink: item.customButtonEnabled ? item.customButtonLink : undefined,
        })),
      };
      const timeRange = values.timeRange as Dayjs[] | undefined;
      if (values.timeType === 'specified') {
        payload.startTime = timeRange?.[0]?.format('YYYY-MM-DD HH:mm:ss');
        payload.endTime = timeRange?.[1]?.format('YYYY-MM-DD HH:mm:ss');
      }
      if (mode === 'edit' && activityId) {
        payload.activityId = activityId;
      }
      setSubmitLoading(true);
      const api = mode === 'edit' ? benefitUpdateV2 : benefitCreateV2;
      const res: any = await api(payload);
      if (res?.code === 200) {
        message.success(mode === 'edit' ? '编辑成功' : '创建成功');
        handleClose();
        onSuccess();
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Drawer
      destroyOnClose
      width={820}
      open={open}
      title={`${mode === 'create' ? '新建' : mode === 'view' ? '查看' : '编辑'}-宠物建档礼`}
      placement="right"
      onClose={handleClose}
      footer={null}
      extra={
        !isView ? (
          <Button
            type="primary"
            loading={submitLoading}
            onClick={handleSubmit}>
            保存
          </Button>
        ) : null
      }>
      <Spin spinning={detailLoading}>
        <Alert
          message="活动注意事项"
          description={
            <ol style={{ marginBottom: 0, paddingInlineStart: 20 }}>
              <li>该活动只适用于天猫店铺,且需为陆泽直接管理的会员通店铺</li>
              <li>该活动只支持天猫运营后台创建的会员专享奖励</li>
            </ol>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ style: { width: 120 } }}
          wrapperCol={{ style: { flex: 1 } }}
          initialValues={createInitialValues()}>
          <Form.Item
            label="活动名称"
            name="activityName"
            rules={[{ required: true, message: '请输入活动名称' }]}>
            <Input
              placeholder="请输入活动名称"
              disabled={isView}
            />
          </Form.Item>
          <Form.Item
            label="活动背景图"
            name="backImage"
            rules={[uploadRequiredRule('请上传活动背景图')]}>
            <ImageUploader
              disabled={isView}
              width={320}
              height={160}
              maxSizeKB={3072}
              tip="大小：不超过3M"
            />
          </Form.Item>
          <Form.Item
            label="活动简介"
            name="activityDesc"
            rules={[{ required: true, message: '请输入活动简介' }]}>
            <Input
              placeholder="请输入活动简介"
              disabled={isView}
            />
          </Form.Item>
          <Form.Item
            label="活动时间"
            required>
            <Space align="start">
              <Form.Item
                name="timeType"
                noStyle
                rules={[{ required: true, message: '请选择活动时间类型' }]}>
                <Radio.Group disabled={isView}>
                  <Radio value="permanent">长期有效</Radio>
                  <Radio value="specified">指定时间</Radio>
                </Radio.Group>
              </Form.Item>
              {timeType === 'specified' ? (
                <Form.Item
                  name="timeRange"
                  noStyle
                  rules={[{ required: true, message: '请选择活动时间' }]}>
                  <RangePicker
                    showTime
                    disabled={isView}
                    format="YYYY-MM-DD HH:mm:ss"
                  />
                </Form.Item>
              ) : null}
            </Space>
          </Form.Item>
          <Form.Item
            label="活动状态"
            name="activityStatus"
            rules={[{ required: true, message: '请选择活动状态' }]}>
            <Radio.Group disabled={isView}>
              <Radio value="disabled">停用</Radio>
              <Radio value="enabled">启用</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.List name="benefitList">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => {
                  return (
                    <div
                      key={field.key}
                      style={{
                        background: '#f7f7f7',
                        borderRadius: 12,
                        padding: 20,
                        marginBottom: 16,
                        border: '1px solid #f0f0f0',
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 16,
                        }}>
                        <div style={{ fontSize: 18, fontWeight: 600 }}>
                          权益{BENEFIT_INDEX_TEXT[index] || index + 1}
                        </div>
                        {!isView && fields.length > 1 ? (
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        ) : null}
                      </div>
                      <Form.Item
                        label="权益类型"
                        name={[field.name, 'benefitType']}
                        rules={[{ required: true, message: '请选择权益类型' }]}>
                        <Radio.Group
                          disabled={isView}
                          options={BENEFIT_TYPE_OPTIONS}
                          onChange={e => {
                            const nextType = e.target.value;
                            const currentCouponId = form.getFieldValue(['benefitList', field.name, 'couponId']);
                            const reductionCouponId = form.getFieldValue([
                              'benefitList',
                              field.name,
                              'reductionCouponId',
                            ]);
                            const physicalCouponId = form.getFieldValue([
                              'benefitList',
                              field.name,
                              'physicalCouponId',
                            ]);

                            if (nextType === 'coupon_full_reduction') {
                              form.setFieldValue(
                                ['benefitList', field.name, 'physicalCouponId'],
                                currentCouponId || physicalCouponId,
                              );
                              form.setFieldValue(
                                ['benefitList', field.name, 'couponId'],
                                reductionCouponId || undefined,
                              );
                              return;
                            }

                            form.setFieldValue(
                              ['benefitList', field.name, 'reductionCouponId'],
                              currentCouponId || reductionCouponId,
                            );
                            form.setFieldValue(['benefitList', field.name, 'couponId'], physicalCouponId || undefined);
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => {
                          return (
                            prevValues?.benefitList?.[field.name]?.benefitType !==
                            currentValues?.benefitList?.[field.name]?.benefitType
                          );
                        }}>
                        {() =>
                          form.getFieldValue(['benefitList', field.name, 'benefitType']) === 'coupon_full_reduction' ? (
                            <Form.Item
                              label="选择券"
                              name={[field.name, 'couponId']}
                              rules={[{ required: true, message: '请选择满减券' }]}>
                              <Select
                                placeholder="请选择满减券"
                                disabled={isView}
                                loading={couponLoading}
                                options={couponOptions}
                                showSearch
                                optionFilterProp="label"
                                onChange={value => {
                                  form.setFieldValue(['benefitList', field.name, 'reductionCouponId'], value);
                                }}
                                onOpenChange={nextOpen => {
                                  if (nextOpen && shopId) {
                                    fetchCouponOptions(shopId);
                                  }
                                }}
                              />
                            </Form.Item>
                          ) : null
                        }
                      </Form.Item>
                      <Form.Item
                        label="权益图片"
                        required
                        name={[field.name, 'benefitImage']}
                        rules={[uploadRequiredRule('请上传权益图片')]}>
                        <ImageUploader
                          disabled={isView}
                          maxSizeKB={3072}
                          tip="宽高一致，大小：不超过3M"
                        />
                      </Form.Item>
                      <Form.Item
                        label="权益主标题"
                        name={[field.name, 'benefitTitle']}
                        rules={[{ required: true, message: '请输入权益主标题' }]}>
                        <Input
                          placeholder="请输入"
                          disabled={isView}
                          maxLength={14}
                          showCount={INPUT_COUNT_PROPS}
                        />
                      </Form.Item>
                      <Form.Item
                        label="权益副标题"
                        name={[field.name, 'benefitSubTitle']}
                        rules={[{ required: true, message: '请输入权益副标题' }]}>
                        <Input
                          placeholder="请输入"
                          disabled={isView}
                          maxLength={18}
                          showCount={INPUT_COUNT_PROPS}
                        />
                      </Form.Item>
                      <Form.Item
                        label={
                          <Space size={4}>
                            <Tooltip title="建档礼权益领取后的按钮文案，需要自定义配置">
                              <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'pointer' }} />
                            </Tooltip>
                            <span>按钮文案</span>
                          </Space>
                        }
                        name={[field.name, 'actionButtonText']}
                        rules={[{ required: true, message: '请输入按钮文案' }]}>
                        <Input
                          placeholder="请输入"
                          disabled={isView}
                          maxLength={4}
                          showCount={INPUT_COUNT_PROPS}
                        />
                      </Form.Item>
                      <Form.Item
                        label="按钮跳转"
                        name={[field.name, 'actionButtonLink']}>
                        <Input
                          placeholder="请输入"
                          disabled={isView}
                        />
                      </Form.Item>
                      <Form.Item
                        label={POPUP_BUTTON_TEXT_LABEL}
                        name={[field.name, 'popupButtonText']}>
                        <Input
                          placeholder="请输入"
                          disabled={isView}
                          maxLength={6}
                          showCount={INPUT_COUNT_PROPS}
                        />
                      </Form.Item>
                      <Form.Item
                        label="弹窗按钮跳转"
                        name={[field.name, 'popupButtonLink']}>
                        <Input
                          placeholder="请输入"
                          disabled={isView}
                        />
                      </Form.Item>
                      <Form.Item
                        label=" "
                        colon={false}
                        valuePropName="checked"
                        name={[field.name, 'customButtonEnabled']}>
                        <Checkbox
                          disabled={isView}
                          onChange={e => {
                            if (!e.target.checked) {
                              form.setFieldValue(['benefitList', field.name, 'customButtonText'], '');
                              form.setFieldValue(['benefitList', field.name, 'customButtonLink'], '');
                            }
                          }}>
                          增加自定义跳转按钮
                        </Checkbox>
                      </Form.Item>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => {
                          return (
                            prevValues?.benefitList?.[field.name]?.customButtonEnabled !==
                            currentValues?.benefitList?.[field.name]?.customButtonEnabled
                          );
                        }}>
                        {() =>
                          form.getFieldValue(['benefitList', field.name, 'customButtonEnabled']) ? (
                            <div
                              style={{
                                background: '#fff',
                                border: '1px solid #e8e8e8',
                                borderRadius: 10,
                                padding: '16px 16px 0',
                              }}>
                              <Form.Item
                                label="自定义按钮文案"
                                name={[field.name, 'customButtonText']}
                                rules={[{ required: true, message: '请输入自定义按钮文案' }]}>
                                <Input
                                  placeholder="请输入"
                                  disabled={isView}
                                  maxLength={6}
                                  showCount={INPUT_COUNT_PROPS}
                                />
                              </Form.Item>
                              <Form.Item
                                label="按钮跳转"
                                name={[field.name, 'customButtonLink']}
                                rules={[{ required: true, message: '请输入按钮跳转链接' }]}>
                                <Input
                                  placeholder="请输入"
                                  disabled={isView}
                                />
                              </Form.Item>
                            </div>
                          ) : null
                        }
                      </Form.Item>
                    </div>
                  );
                })}
                {!isView && fields.length < MAX_BENEFIT_COUNT ? (
                  <Button
                    style={{ marginBottom: 24 }}
                    onClick={() => add(createDefaultBenefit())}>
                    添加权益（{fields.length}/{MAX_BENEFIT_COUNT}）
                  </Button>
                ) : null}
              </>
            )}
          </Form.List>

          <Form.Item
            label="权益说明"
            required>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ pointerEvents: isView ? 'none' : 'auto', opacity: isView ? 0.7 : 1 }}>
                <Toolbar
                  editor={editor}
                  defaultConfig={toolbarConfig as any}
                />
              </div>
              <Editor
                defaultConfig={{ ...defaultEditorConfig, readOnly: isView, maxLength: 1000 }}
                value={editorHtml}
                onCreated={setEditor}
                onChange={(currentEditor: any) => {
                  setEditorHtml(currentEditor.getHtml());
                }}
                mode="default"
                style={{ minHeight: 220, overflowY: 'hidden' }}
              />
            </div>
          </Form.Item>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default PetProfileV2Drawer;
